import React from 'react';
import { KlineCandle } from '../services/klineTypes';
import { BarInterval, AppPage } from '@/services/enums';
import { BacktestResult } from '../services/backtestService';

interface KlinePlaybackControlsProps {
  bar: BarInterval;
  setBar: (bar: BarInterval) => void;
  speed: number;
  setSpeed: (speed: number) => void;
  isPlaying: boolean;
  playPause: () => void;
  stop: () => void;
  prevCandle: () => void;
  nextCandle: () => void;
  progress: number;
  handleProgressChange: (progress: number) => void;
  currentTime: Date | null;
  allCandles: KlineCandle[];
  currentIndex: number;
  loading: boolean;
  backtestList: BacktestResult[];
  selectedBacktestId: number | null;
  setSelectedBacktestId: (id: number | null) => void;
  visibleRange: { from: number; to: number } | null;
  setPlaybackRange: (range: { from: number; to: number; bar: BarInterval }) => void;
  setCurrentPage: (page: AppPage) => void;
}

const KlinePlaybackControls: React.FC<KlinePlaybackControlsProps> = ({
  bar,
  setBar,
  speed,
  setSpeed,
  isPlaying,
  playPause,
  stop,
  prevCandle,
  nextCandle,
  progress,
  handleProgressChange,
  currentTime,
  allCandles,
  currentIndex,
  loading,
  backtestList,
  selectedBacktestId,
  setSelectedBacktestId,
  visibleRange,
  setPlaybackRange,
  setCurrentPage,
}) => {
  return (
    <div className="bg-black/20 backdrop-blur-sm border-t border-white/10 px-4 py-3 z-40">
      {/* 第一行：时间粒度和速度选择 */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-1">
          <span className="text-white/80 text-xs whitespace-nowrap">粒度:</span>
          <select
            value={bar}
            onChange={(e) => setBar(e.target.value as BarInterval)}
            className="bg-black/30 border border-white/20 rounded px-1.5 py-1 text-white text-xs w-14 appearance-none cursor-pointer hover:bg-black/40 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.35rem center',
              paddingRight: '1.5rem',
            }}
          >
            <option value="5m" className="bg-gray-800 text-white">
              5m
            </option>
            <option value="15m" className="bg-gray-800 text-white">
              15m
            </option>
            <option value="30m" className="bg-gray-800 text-white">
              30m
            </option>
            <option value="1H" className="bg-gray-800 text-white">
              1H
            </option>
            <option value="4H" className="bg-gray-800 text-white">
              4H
            </option>
            <option value="1D" className="bg-gray-800 text-white">
              1D
            </option>
          </select>
        </div>

        <div className="flex items-center gap-1">
          <span className="text-white/80 text-xs whitespace-nowrap">速度:</span>
          <select
            value={speed}
            onChange={(e) => setSpeed(parseFloat(e.target.value))}
            className="bg-black/30 border border-white/20 rounded px-1.5 py-1 text-white text-xs w-14 appearance-none cursor-pointer hover:bg-black/40 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isPlaying}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.35rem center',
              paddingRight: '1.5rem',
            }}
          >
            <option value={0.25} className="bg-gray-800 text-white">
              0.25x
            </option>
            <option value={0.5} className="bg-gray-800 text-white">
              0.5x
            </option>
            <option value={1} className="bg-gray-800 text-white">
              1x
            </option>
            <option value={2} className="bg-gray-800 text-white">
              2x
            </option>
            <option value={4} className="bg-gray-800 text-white">
              4x
            </option>
          </select>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <select
            value={selectedBacktestId || ''}
            onChange={(e) => setSelectedBacktestId(e.target.value ? parseInt(e.target.value) : null)}
            className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:ring-1 focus:ring-white/30 max-w-[120px]"
          >
            <option value="" className="bg-gray-800">
              回测记录
            </option>
            {backtestList.map((bt) => (
              <option key={bt.id} value={bt.id} className="bg-gray-800">
                {bt.strategy?.name || bt.name}
              </option>
            ))}
          </select>

          <button
            onClick={() => {
              if (visibleRange) {
                setPlaybackRange({
                  from: visibleRange.from,
                  to: visibleRange.to,
                  bar,
                });
              } else if (allCandles.length > 0) {
                const first = allCandles[0].time;
                const last = allCandles[allCandles.length - 1].time;
                const from = typeof first === 'number' ? first : parseInt(String(first));
                const to = typeof last === 'number' ? last : parseInt(String(last));
                setPlaybackRange({
                  from,
                  to,
                  bar,
                });
              }
              setCurrentPage('backtest');
            }}
            className="px-2 py-1 bg-purple-500/20 border border-purple-500/50 rounded text-purple-200 text-xs hover:bg-purple-500/30 transition-colors whitespace-nowrap"
          >
            回测
          </button>
        </div>
      </div>

      {/* 第二行：播放控制按钮 */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={playPause}
          disabled={allCandles.length === 0 || loading}
          className="px-3 py-1.5 bg-blue-500/20 border border-blue-500/50 rounded-lg text-white text-sm hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {isPlaying ? '⏸ 暂停' : '▶ 播放'}
        </button>
        <button
          onClick={stop}
          disabled={allCandles.length === 0 || loading}
          className="px-3 py-1.5 bg-gray-500/20 border border-gray-500/50 rounded-lg text-white text-sm hover:bg-gray-500/30 disabled:opacity-50 whitespace-nowrap"
        >
          ⏹ 停止
        </button>
        <button
          onClick={prevCandle}
          disabled={currentIndex <= 0 || loading}
          className="px-3 py-1.5 bg-gray-500/20 border border-gray-500/50 rounded-lg text-white text-sm hover:bg-gray-500/30 disabled:opacity-50 whitespace-nowrap"
        >
          ⏮ 后退
        </button>
        <button
          onClick={nextCandle}
          disabled={currentIndex >= allCandles.length - 1 || loading || isPlaying}
          className="px-3 py-1.5 bg-gray-500/20 border border-gray-500/50 rounded-lg text-white text-sm hover:bg-gray-500/30 disabled:opacity-50 whitespace-nowrap"
        >
          前进 ⏭
        </button>
      </div>

      {/* 第三行：进度条 */}
      <div className="mb-2">
        <input
          type="range"
          min="0"
          max="100"
          value={progress}
          onChange={(e) => handleProgressChange(parseFloat(e.target.value))}
          className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, rgba(59, 130, 246, 0.5) 0%, rgba(59, 130, 246, 0.5) ${progress}%, rgba(255, 255, 255, 0.1) ${progress}%, rgba(255, 255, 255, 0.1) 100%)`,
          }}
          disabled={allCandles.length === 0 || loading}
        />
        <div className="flex justify-between items-center text-xs text-white/60 mt-1">
          <span>0%</span>
          <span className="text-white/80 font-medium">{progress.toFixed(1)}%</span>
          <span>100%</span>
        </div>
        {currentTime && (
          <div className="text-center text-white/80 text-sm mt-1">
            {currentTime.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })}
          </div>
        )}
      </div>

      {/* 第四行：底部信息 */}
      <div className="flex justify-between items-center text-xs text-white/60 pt-2 border-t border-white/10">
        <span>总K线数: {allCandles.length}</span>
        <span>
          当前: {currentIndex + 1} / {allCandles.length}
        </span>
        <span className="hidden sm:inline">快捷键: 空格=播放/暂停, ←=后退, →=前进</span>
      </div>
    </div>
  );
};

export default KlinePlaybackControls;
